import { useMemo, useCallback, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { createResource } from '@diggens/api-client';
import { useCurrentUser } from '@diggens/user-management';
import type { Node } from '@diggens/types';
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridCellParams, type GridRenderEditCellParams } from '@mui/x-data-grid';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { useNodeWithRelationships, useUpdateEdge, useNodes, usePicklists } from '@diggens/shared-hooks';
import { formatDate, formatDateTime, formatNumber } from '@diggens/shared-utils';
import type { RelationshipSectionConfig } from '@diggens/types/graph/nodes';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { useSchema } from '../../contexts';
import { useToast } from '../../contexts/ToastContext';
import { NodeDrawer, type NodeLink } from './NodeDrawer';

dayjs.extend(utc);

/** A section icon by Lucide name, so app-defined sections can use any icon. */
function sectionIcon(iconName: string): React.ComponentType<{ size?: number; strokeWidth?: number }> | undefined {
  const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }> | undefined>;
  return icons[iconName];
}

interface RelationshipsSectionProps {
  nodeId: string;
  nodeType: string;
}

interface OutgoingEdge {
  id: string;
  type: string;
  props: Record<string, unknown>;
  dst_node: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  };
}

interface IncomingEdge {
  id: string;
  type: string;
  props: Record<string, unknown>;
  src_node: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  };
}

interface ProcessedRelationship {
  id: string;
  edgeId: string;
  edgeType: string;
  edgeProps: Record<string, unknown>;
  nodeId: string;
  nodeType: string;
  nodeProps: Record<string, unknown>;
  name: string;
}

/**
 * Process edges into a flat list of relationships for a section
 */
function processEdgesForSection(config: RelationshipSectionConfig, outgoingEdges: OutgoingEdge[], incomingEdges: IncomingEdge[]): ProcessedRelationship[] {
  const relationships: ProcessedRelationship[] = [];
  if (config.direction === 'outgoing' || config.direction === 'both') {
    outgoingEdges
      .filter((edge) => config.edgeTypes.includes(edge.type))
      .filter((edge) => !config.relatedNodeTypes || config.relatedNodeTypes.includes(edge.dst_node.type))
      .forEach((edge) => {
        relationships.push({
          id: edge.id,
          edgeId: edge.id,
          edgeType: edge.type,
          edgeProps: edge.props,
          nodeId: edge.dst_node.id,
          nodeType: edge.dst_node.type,
          nodeProps: edge.dst_node.props,
          name: (edge.dst_node.props.name as string) || (edge.dst_node.props.subject as string) || 'Unnamed',
        });
      });
  }
  if (config.direction === 'incoming' || config.direction === 'both') {
    incomingEdges
      .filter((edge) => config.edgeTypes.includes(edge.type))
      .filter((edge) => !config.relatedNodeTypes || config.relatedNodeTypes.includes(edge.src_node.type))
      .forEach((edge) => {
        relationships.push({
          id: edge.id,
          edgeId: edge.id,
          edgeType: edge.type,
          edgeProps: edge.props,
          nodeId: edge.src_node.id,
          nodeType: edge.src_node.type,
          nodeProps: edge.src_node.props,
          name: (edge.src_node.props.name as string) || (edge.src_node.props.subject as string) || 'Unnamed',
        });
      });
  }
  return relationships;
}

/**
 * Grid renderer for relationship sections with inline editing
 */
function RelationshipGrid({ relationships, config, onNavigate }: { relationships: ProcessedRelationship[]; config: RelationshipSectionConfig; onNavigate: (type: string, id: string) => void }) {
  const updateEdgeMutation = useUpdateEdge();
  const { getNodeDefinition } = useSchema();
  const { data: allPicklists } = usePicklists();

  // Picklist labels for node columns whose field is a picklist select on the related type
  const picklistLabels = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const picklist of allPicklists ?? []) {
      map[picklist.name] = Object.fromEntries((picklist.items as Array<{ id: string; label: string }>).map((item) => [item.id, item.label]));
    }
    return map;
  }, [allPicklists]);
  const labelFor = useCallback(
    (row: ProcessedRelationship, field: string, value: unknown): string | null => {
      if (typeof value !== 'string') return null;
      const picklistName = getNodeDefinition(row.nodeType)?.formFields.find((candidate) => candidate.key === field)?.picklistName;
      return picklistName ? (picklistLabels[picklistName]?.[value] ?? null) : null;
    },
    [getNodeDefinition, picklistLabels]
  );

  const handleCellClick = useCallback(
    (params: GridCellParams<ProcessedRelationship>) => {
      // Only navigate on first column (node link) click
      if (params.field === config.columns?.[0]?.field) {
        onNavigate(params.row.nodeType, params.row.nodeId);
      }
    },
    [config.columns, onNavigate]
  );

  const handleProcessRowUpdate = useCallback(
    async (newRow: ProcessedRelationship, oldRow: ProcessedRelationship) => {
      // Find which field changed
      const changedFields = Object.keys(newRow).filter((key) => newRow[key as keyof ProcessedRelationship] !== oldRow[key as keyof ProcessedRelationship]);

      if (changedFields.length === 0) return oldRow;

      // Update edge props
      const updatedProps = { ...oldRow.edgeProps };
      changedFields.forEach((field) => {
        updatedProps[field] = newRow[field as keyof ProcessedRelationship];
      });

      await updateEdgeMutation.mutateAsync({
        edgeId: oldRow.edgeId,
        updates: { props: updatedProps },
      });

      return newRow;
    },
    [updateEdgeMutation]
  );

  const columns: GridColDef[] = useMemo(() => {
    if (!config.columns) return [];
    return config.columns.map((col, index) => {
      const isFirstColumn = index === 0;

      return {
        ...col,
        type: undefined,
        editable: col.editable ?? false,
        valueGetter: (_value: unknown, row: ProcessedRelationship) => {
          if (col.field === 'name') return row.name;
          if (col.field === 'type') return row.nodeType;
          // Get from correct source - return raw value
          const rawValue = col.source === 'edge' ? row.edgeProps[col.field] : row.nodeProps[col.field];
          return rawValue ?? '';
        },
        valueFormatter: (value: unknown, row: ProcessedRelationship) => {
          if (!value) return '';
          if (col.source !== 'edge') {
            const label = labelFor(row, col.field, value);
            if (label) return label;
          }
          // Format dates for display only
          if (col.type === 'datetime' && typeof value === 'string') {
            return formatDateTime(value);
          }
          if (col.type === 'date' && typeof value === 'string') {
            return formatDate(value);
          }
          // Format numbers with currency/percentage/year if specified
          if (col.type === 'number' && typeof value === 'number' && (col as any).format) {
            return formatNumber(value, (col as any).format);
          }
          return String(value);
        },
        renderCell: isFirstColumn
          ? (params: GridRenderCellParams<ProcessedRelationship>) => (
              <Box
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {params.value}
              </Box>
            )
          : undefined,
        renderEditCell:
          col.type === 'date' || col.type === 'datetime'
            ? (params: GridRenderEditCellParams<ProcessedRelationship>) => {
                const Picker = col.type === 'datetime' ? DateTimePicker : DatePicker;
                return (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Picker
                      value={params.value ? (col.type === 'date' ? dayjs.utc(params.value as string) : dayjs(params.value as string)) : null}
                      onChange={(newValue) => {
                        const formattedValue = col.type === 'datetime' ? newValue?.toISOString() || '' : newValue?.format('YYYY-MM-DD') || '';
                        params.api.setEditCellValue({
                          id: params.id,
                          field: params.field,
                          value: formattedValue,
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          variant: 'standard',
                        },
                        openPickerIcon: {
                          sx: { fontSize: '1rem' },
                        },
                      }}
                    />
                  </LocalizationProvider>
                );
              }
            : undefined,
      };
    });
  }, [config.columns, labelFor]);
  return (
    <DataGrid
      rows={relationships}
      columns={columns}
      density="compact"
      autoHeight
      pageSizeOptions={[5, 10, 25]}
      initialState={{
        pagination: { paginationModel: { pageSize: 5 } },
      }}
      onCellClick={handleCellClick}
      processRowUpdate={handleProcessRowUpdate}
      disableRowSelectionOnClick
      sx={{
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell--editable': {
          cursor: 'text',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
    />
  );
}

/** What a section's Add control can link or create: one entry per edge type and related node type the section covers. */
interface AddTarget {
  edgeType: string;
  nodeType: string;
  /** The edge's direction from the new or linked record's side. */
  direction: 'outgoing' | 'incoming';
  label: string;
}

/** Link an existing record of the target type to this one: search, pick, and write the edge. */
function LinkExistingDialog({ target, nodeId, onClose }: { target: AddTarget; nodeId: string; onClose: () => void }) {
  const { data: currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { getNodeDefinition } = useSchema();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Node | null>(null);
  const [saving, setSaving] = useState(false);
  const { data: results, isLoading } = useNodes(target.nodeType, { search: search || undefined, pageSize: 20, sortBy: 'created_at', sortOrder: 'desc' });
  const titleField = getNodeDefinition(target.nodeType)?.formFields[0]?.key ?? 'name';
  const nameOf = (node: Node) => (node.props[titleField] as string) || (node.props.name as string) || (node.props.title as string) || 'Untitled';

  const link = async () => {
    if (!picked || !currentUser?.tenant_id) return;
    setSaving(true);
    try {
      await createResource('edges', {
        tenant_id: currentUser.tenant_id,
        type: target.edgeType,
        src: target.direction === 'outgoing' ? picked.id : nodeId,
        dst: target.direction === 'outgoing' ? nodeId : picked.id,
        props: {},
      });
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships'] });
      queryClient.invalidateQueries({ queryKey: ['edges'] });
      showToast(`${nameOf(picked)} linked`, 'success');
      onClose();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not link the record', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Link an existing {target.label.toLowerCase()}</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={results?.data ?? []}
          loading={isLoading}
          value={picked}
          onChange={(_event, value) => setPicked(value)}
          onInputChange={(_event, value) => setSearch(value)}
          getOptionLabel={nameOf}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label={target.label} placeholder="Type to search" autoFocus />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={link} disabled={!picked || saving}>
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Single relationship section, with an Add control that links an existing record or creates a new one already linked.
 */
function RelationshipSection({ config, relationships, nodeId, onNavigate }: { config: RelationshipSectionConfig; relationships: ProcessedRelationship[]; nodeId: string; onNavigate: (type: string, id: string) => void }) {
  const IconComponent = sectionIcon(config.icon);
  const { getEdgeDefinition, getNodeDefinition } = useSchema();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [linking, setLinking] = useState<AddTarget | null>(null);
  const [creating, setCreating] = useState<AddTarget | null>(null);

  // Every edge type and related node type the section covers; the related record's side of the edge is the opposite of ours
  const targets = useMemo<AddTarget[]>(() => {
    const list: AddTarget[] = [];
    const directions: Array<'outgoing' | 'incoming'> = config.direction === 'both' ? ['outgoing', 'incoming'] : [config.direction];
    for (const edgeType of config.edgeTypes) {
      const edge = getEdgeDefinition(edgeType);
      for (const direction of directions) {
        const candidates = direction === 'outgoing' ? edge?.targetTypes ?? [] : edge?.sourceTypes ?? [];
        const nodeTypes = (config.relatedNodeTypes ?? candidates.filter((type) => type !== '*')).filter((type) => candidates.length === 0 || candidates.includes('*') || candidates.includes(type));
        for (const nodeType of nodeTypes) {
          const label = getNodeDefinition(nodeType)?.displayName ?? nodeType;
          list.push({ edgeType, nodeType, direction: direction === 'outgoing' ? 'incoming' : 'outgoing', label });
        }
      }
    }
    return list;
  }, [config, getEdgeDefinition, getNodeDefinition]);

  const linkFor = (target: AddTarget): NodeLink => ({ edgeType: target.edgeType, direction: target.direction, otherNodeId: nodeId });

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {IconComponent && <IconComponent size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}
        <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1 }}>
          {config.sectionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, flex: 1 }}>
          ({relationships.length})
        </Typography>
        {targets.length > 0 && (
          <Button size="small" startIcon={<Plus size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={(event) => setMenuAnchor(event.currentTarget)}>
            Add
          </Button>
        )}
      </Box>
      <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
        {targets.map((target) => (
          <MenuItem
            key={`link-${target.edgeType}-${target.nodeType}-${target.direction}`}
            onClick={() => {
              setMenuAnchor(null);
              setLinking(target);
            }}
          >
            Link existing {target.label.toLowerCase()}
          </MenuItem>
        ))}
        {targets.map((target) => (
          <MenuItem
            key={`new-${target.edgeType}-${target.nodeType}-${target.direction}`}
            onClick={() => {
              setMenuAnchor(null);
              setCreating(target);
            }}
          >
            Create new {target.label.toLowerCase()}
          </MenuItem>
        ))}
      </Menu>
      {linking && <LinkExistingDialog target={linking} nodeId={nodeId} onClose={() => setLinking(null)} />}
      {creating && <NodeDrawer open onClose={() => setCreating(null)} nodeType={creating.nodeType} mode="create" linkTo={linkFor(creating)} />}
      <RelationshipGrid relationships={relationships} config={config} onNavigate={onNavigate} />
    </Box>
  );
}

export function RelationshipsSection({ nodeId, nodeType }: RelationshipsSectionProps) {
  const navigate = useNavigate();
  const { data: relationships, isLoading } = useNodeWithRelationships(nodeId);
  const { getNodeDefinition, getListPath } = useSchema();

  const sectionConfigs = useMemo(() => getNodeDefinition(nodeType)?.relationshipSections ?? [], [nodeType, getNodeDefinition]);

  // Transform the relationships data to match our expected format
  const outgoingEdges: OutgoingEdge[] = useMemo(() => {
    if (!relationships?.outgoing) return [];
    return relationships.outgoing
      .filter((rel: any) => rel.targetNode)
      .map((rel: any) => ({
        id: rel.edge.id,
        type: rel.edge.type,
        props: rel.edge.props,
        dst_node: {
          id: rel.targetNode.id,
          type: rel.targetNode.type,
          props: rel.targetNode.props,
        },
      }));
  }, [relationships]);

  const incomingEdges: IncomingEdge[] = useMemo(() => {
    if (!relationships?.incoming) return [];
    return relationships.incoming
      .filter((rel: any) => rel.sourceNode)
      .map((rel: any) => ({
        id: rel.edge.id,
        type: rel.edge.type,
        props: rel.edge.props,
        src_node: {
          id: rel.sourceNode.id,
          type: rel.sourceNode.type,
          props: rel.sourceNode.props,
        },
      }));
  }, [relationships]);

  // Every section shows, empty ones included, so a record can get its first relationship from here
  const sectionsWithData = useMemo(() => {
    return sectionConfigs.map((config) => ({
      config,
      relationships: processEdgesForSection(config, outgoingEdges, incomingEdges),
    }));
  }, [sectionConfigs, outgoingEdges, incomingEdges]);

  const handleNavigate = (type: string, id: string) => {
    // An installed app's nav or a module route knows the list page; the map below covers built-ins without one.
    const listPath = getListPath(type);
    if (listPath) {
      navigate(`${listPath}/${id}`);
      return;
    }
    // Routes use plural form (e.g., /deals/, /people/, /opportunities/)
    const routeMap: Record<string, string> = {
      // Irregular plurals
      Person: 'people',
      Opportunity: 'opportunities',
      Activity: 'activities',
      // Hyphenated routes
      FundraisingOpportunity: 'fundraising',
      ExternalFund: 'external-funds',
      CapitalCall: 'capital-calls',
      InvestmentAccount: 'investment-accounts',
      PortfolioCompany: 'portfolio-companies',
      // Contacts alias
      Contact: 'contacts',
    };
    const route = routeMap[type] || `${type.toLowerCase()}s`;
    navigate(`/${route}/${id}`);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (sectionsWithData.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">No relationship sections are defined for this type</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {sectionsWithData.map(({ config, relationships }) => (
        <RelationshipSection key={config.sectionLabel} config={config} relationships={relationships} nodeId={nodeId} onNavigate={handleNavigate} />
      ))}
    </Paper>
  );
}
